
import { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts';

interface Node {
  id: string;
  name: string;
  category: number;
  value: number;
  x?: number;
  y?: number;
  description?: string;
}

interface Link {
  source: string;
  target: string;
  value?: number;
}

interface KnowledgeGraphChartProps {
  nodes: Node[];
  links: Link[];
  categories: { name: string; color: string }[];
  onNodeClick?: (node: Node) => void;
  height?: number;
}

export default function KnowledgeGraphChart({
  nodes,
  links,
  categories,
  onNodeClick,
  height = 500
}: KnowledgeGraphChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    if (!chartRef.current) return;

    // 初始化图表
    chartInstance.current = echarts.init(chartRef.current);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          if (params.dataType === 'node') {
            return `
              <div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${params.data.name}</div>
                <div style="color: #666; font-size: 12px;">${params.data.description || '暂无描述'}</div>
              </div>
            `;
          }
          return '';
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: 'transparent',
        textStyle: {
          color: '#fff'
        }
      },
      legend: {
        data: categories.map(cat => cat.name),
        orient: 'horizontal',
        left: 'center',
        top: 10,
        textStyle: {
          color: '#333'
        }
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          data: nodes.map(node => ({
            ...node,
            symbolSize: 40 + node.value * 10,
            itemStyle: {
              color: categories[node.category]?.color || '#5470c6'
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 10,
              fontWeight: 'bold',
              color: '#fff',
              formatter: (params: any) => {
                const name = params.data.name;
                return name.length > 6 ? name.substring(0, 6) + '...' : name;
              }
            }
          })),
          links: links.map(link => ({
            ...link,
            lineStyle: {
              color: '#94a3b8',
              width: 2,
              type: 'dashed'
            }
          })),
          categories: categories.map(cat => ({
            name: cat.name,
            itemStyle: {
              color: cat.color
            }
          })),
          roam: true,
          focusNodeAdjacency: true,
          draggable: true,
          force: {
            repulsion: 1000,
            gravity: 0.1,
            edgeLength: 150,
            layoutAnimation: true
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: {
              width: 4
            }
          },
          animation: true,
          animationDuration: 1000,
          animationEasing: 'cubicOut'
        }
      ]
    };

    chartInstance.current.setOption(option);

    // 添加点击事件
    chartInstance.current.on('click', (params: any) => {
      if (params.dataType === 'node' && onNodeClick) {
        onNodeClick(params.data);
      }
    });

    // 响应式处理
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, [nodes, links, categories, onNodeClick]);

  // 缩放功能
  const handleZoomIn = () => {
    if (chartInstance.current) {
      const newZoom = Math.min(zoomLevel * 1.2, 3);
      setZoomLevel(newZoom);
      chartInstance.current.dispatchAction({
        type: 'restore'
      });
      setTimeout(() => {
        chartInstance.current?.dispatchAction({
          type: 'dataZoom',
          zoom: newZoom
        });
      }, 100);
    }
  };

  const handleZoomOut = () => {
    if (chartInstance.current) {
      const newZoom = Math.max(zoomLevel / 1.2, 0.5);
      setZoomLevel(newZoom);
      chartInstance.current.dispatchAction({
        type: 'restore'
      });
      setTimeout(() => {
        chartInstance.current?.dispatchAction({
          type: 'dataZoom',
          zoom: newZoom
        });
      }, 100);
    }
  };

  const handleResetZoom = () => {
    if (chartInstance.current) {
      setZoomLevel(1);
      chartInstance.current.dispatchAction({
        type: 'restore'
      });
    }
  };

  const handleFullscreen = () => {
    if (chartRef.current) {
      if (chartRef.current.requestFullscreen) {
        chartRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div className="relative">
      {/* 缩放控制按钮 */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 bg-white shadow-lg rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          title="放大"
        >
          <i className="ri-add-line text-lg text-gray-700"></i>
        </button>
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 bg-white shadow-lg rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          title="缩小"
        >
          <i className="ri-subtract-line text-lg text-gray-700"></i>
        </button>
        <button
          onClick={handleResetZoom}
          className="w-10 h-10 bg-white shadow-lg rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          title="重置"
        >
          <i className="ri-refresh-line text-lg text-gray-700"></i>
        </button>
        <button
          onClick={handleFullscreen}
          className="w-10 h-10 bg-white shadow-lg rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          title="全屏"
        >
          <i className="ri-fullscreen-line text-lg text-gray-700"></i>
        </button>
      </div>

      {/* 缩放级别显示 */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="bg-white shadow-lg rounded-lg px-3 py-2 text-sm text-gray-600">
          缩放: {Math.round(zoomLevel * 100)}%
        </div>
      </div>

      {/* 图表容器 */}
      <div 
        ref={chartRef} 
        style={{ width: '100%', height: `${height}px` }}
        className="bg-gray-50 rounded-lg"
      />
    </div>
  );
}
